import React, { useState, useCallback, useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, UserCheck, Users, Briefcase, CreditCard,
  LayoutGrid, FileText, GalleryHorizontal, MessageSquare, Bell,
  Settings, ClipboardList, Search, ChevronRight, ChevronLeft, LogOut,
  UserPlus
} from "lucide-react";
import AppLogo from '../components/AppLogo';
import NotificationsDropdown from '../components/NotificationsDropdown';
import { supabase } from '../lib/supabase';
import AdminLogin from './AdminLogin';

// Inject font loader
if (!document.getElementById("__jmf")) {
  const l = document.createElement("link");
  l.id = "__jmf";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Playfair+Display:wght@600;700&display=swap";
  document.head.appendChild(l);
}

const NAV_ITEMS_TOP = [
  { label: "Overview", icon: LayoutDashboard, path: "/admin/overview", permission: "view_overview" },
  { label: "Pros", icon: UserCheck, path: "/admin/pros", permission: "manage_pros" },
  { label: "Signups", icon: UserPlus, path: "/admin/signups", permission: "manage_pros" },
  { label: "Customers", icon: Users, path: "/admin/customers", permission: "manage_customers" },
  { label: "Jobs", icon: Briefcase, path: "/admin/jobs", permission: "manage_jobs" },
  { label: "Payments", icon: CreditCard, path: "/admin/payments", permission: "manage_payments" },
  { label: "Categories", icon: LayoutGrid, path: "/admin/categories", permission: "manage_categories" },
  { label: "Content", icon: FileText, path: "/admin/content", permission: "manage_content" },
  { label: "Media", icon: GalleryHorizontal, path: "/admin/media", permission: "manage_media" },
];

const NAV_ITEMS_BOTTOM = [
  { label: "Messages", icon: MessageSquare, path: "/admin/messages", permission: "view_messages" },
  { label: "Notifications", icon: Bell, path: "/admin/notifications", permission: "view_notifications" },
  { label: "Settings", icon: Settings, path: "/admin/settings", permission: "manage_settings" },
  { label: "Logs", icon: ClipboardList, path: "/admin/logs", permission: "view_logs" },
];

const PAGE_META = {
  "/admin/overview": { title: "Overview", sub: "Platform activity & performance" },
  "/admin/pros": { title: "Pro Management", sub: "Review, approve and manage service providers" },
  "/admin/signups": { title: "Pro Signups", sub: "Leads from the landing page, ahead of full onboarding" },
  "/admin/customers": { title: "Customers", sub: "All registered customers on the platform" },
  "/admin/jobs": { title: "Jobs", sub: "All bookings and job activity" },
  "/admin/payments": { title: "Payments", sub: "Transactions, revenue and payment status" },
  "/admin/categories": { title: "Categories", sub: "Service categories and visibility" },
  "/admin/content": { title: "Content", sub: "Platform content management" },
  "/admin/media": { title: "Media Library", sub: "Images and uploaded files" },
  "/admin/messages": { title: "Messages", sub: "Platform conversations" },
  "/admin/notifications": { title: "Notifications", sub: "Alerts and system notifications" },
  "/admin/settings": { title: "Settings", sub: "Platform configuration" },
  "/admin/logs": { title: "System Logs", sub: "Activity and error logs" },
};

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [toast, setToast] = useState(null);
  const [adminRole, setAdminRole] = useState(null);
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const meta = PAGE_META[location.pathname] || { title: "Admin", sub: "" };

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const openDrawer = useCallback((type, data) => {
    setActiveDrawer({ type, data });
  }, []);

  const closeDrawer = useCallback(() => {
    setActiveDrawer(null);
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setAdminRole(null);
    showToast('Logged out successfully');
  };

  // Check authentication and admin role on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        setSession(currentSession);

        if (currentSession) {
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', currentSession.user.id)
            .single();

          if (error) throw error;
          setAdminRole(profile?.role || null);
          
          if (profile?.role !== 'admin') {
            showToast('Admin privileges required', 'error');
            await supabase.auth.signOut();
            setSession(null);
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
        setSession(null);
        setAdminRole(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) {
        setAdminRole(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate(`/admin/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery("");
    }
  };

  const sidebarWidth = collapsed ? 64 : 220;

  // Show login screen if not authenticated or not admin
  if (!loading && (!session || adminRole !== 'admin')) {
    return <AdminLogin onLoginSuccess={(user) => setSession({ user })} />;
  }

  // Show loading spinner
  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        height: "100vh",
        background: "#F7F8F5"
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 20, marginBottom: 16 }}>🔐</div>
          <div>Loading admin panel...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", height: "100vh", background: "#F7F8F5", overflow: "hidden" }}>
      <style>{`
        .admin-nav-item:hover {
          background: #F9FAFB !important;
        }
        .admin-nav-item:hover svg {
          color: #374151 !important;
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Sidebar */}
      <aside style={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        background: "#FFFFFF",
        borderRight: "1px solid rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        position: "sticky",
        top: 0,
        transition: "width 0.2s ease, min-width 0.2s ease",
        overflowY: "hidden",
        boxSizing: "border-box",
      }}>
        {/* Logo Zone - 48px tall */}
        <div style={{ 
          height: 48, 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          flexShrink: 0,
          marginTop: 8,
        }}>
          {collapsed ? (
            <div style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              background: "#166534",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer"
            }} onClick={() => navigate('/admin/overview')}>
              <span style={{
                fontFamily: "'Playfair Display', serif",
                color: "#fff",
                fontSize: 16,
                fontWeight: 700
              }}>J</span>
            </div>
          ) : (
            <AppLogo size="sm" onClick={() => navigate('/admin/overview')} />
          )}
        </div>

        {/* Top Navigation */}
        <nav style={{ flex: 1, marginTop: 8, overflowY: "visible" }}>
          {NAV_ITEMS_TOP.map(({ label, icon: Icon, path }) => (
            <NavLink
              key={path}
              to={path}
              className="admin-nav-item"
              style={({ isActive }) => ({
                display: "flex",
                alignItems: "center",
                gap: collapsed ? 0 : 12,
                width: collapsed ? 40 : "calc(100% - 16px)",
                height: 32,
                margin: collapsed ? "2px auto" : "2px 8px",
                padding: collapsed ? 0 : "0 12px",
                borderRadius: 10,
                background: isActive ? "#EEF4EF" : "transparent",
                transition: "all 0.15s ease",
                cursor: "pointer",
                textDecoration: "none",
                justifyContent: collapsed ? "center" : "flex-start",
                boxSizing: "border-box",
              })}
            >
              {({ isActive }) => (
                <>
                  <Icon size={18} style={{ color: isActive ? "#166534" : "#9CA3AF", flexShrink: 0 }} />
                  {!collapsed && (
                    <span style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#111827",
                      whiteSpace: "nowrap",
                    }}>{label}</span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Bottom Navigation */}
        <div style={{ marginBottom: 8, flexShrink: 0 }}>
          {NAV_ITEMS_BOTTOM.map(({ label, icon: Icon, path }) => (
            <NavLink
              key={path}
              to={path}
              className="admin-nav-item"
              style={({ isActive }) => ({
                display: "flex",
                alignItems: "center",
                gap: collapsed ? 0 : 12,
                width: collapsed ? 40 : "calc(100% - 16px)",
                height: 32,
                margin: collapsed ? "2px auto" : "2px 8px",
                padding: collapsed ? 0 : "0 12px",
                borderRadius: 10,
                background: isActive ? "#EEF4EF" : "transparent",
                transition: "all 0.15s ease",
                cursor: "pointer",
                textDecoration: "none",
                justifyContent: collapsed ? "center" : "flex-start",
                boxSizing: "border-box",
              })}
            >
              {({ isActive }) => (
                <>
                  <Icon size={18} style={{ color: isActive ? "#166534" : "#9CA3AF", flexShrink: 0 }} />
                  {!collapsed && (
                    <span style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#111827",
                      whiteSpace: "nowrap",
                    }}>{label}</span>
                  )}
                </>
              )}
            </NavLink>
          ))}
          
          {/* Logout Button */}
          <button
            onClick={handleLogout}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-start",
              gap: collapsed ? 0 : 8,
              width: collapsed ? 40 : "calc(100% - 16px)",
              height: 32,
              margin: collapsed ? "2px auto" : "2px 8px",
              padding: collapsed ? 0 : "0 12px",
              borderRadius: 10,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              transition: "all 0.15s ease",
              color: "#DC2626"
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#FEE2E2"}
            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
          >
            <LogOut size={18} style={{ color: "#DC2626", flexShrink: 0 }} />
            {!collapsed && (
              <span style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 12,
                color: "#DC2626",
              }}>Logout</span>
            )}
          </button>
          
          {/* Toggle Button */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-start",
              gap: collapsed ? 0 : 8,
              width: collapsed ? 40 : "calc(100% - 16px)",
              height: 32,
              margin: collapsed ? "8px auto 0 auto" : "8px 8px 0 8px",
              padding: collapsed ? 0 : "0 12px",
              borderRadius: 10,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#F9FAFB"}
            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
          >
            {collapsed ? (
              <ChevronRight size={16} color="#9CA3AF" />
            ) : (
              <>
                <ChevronLeft size={16} color="#9CA3AF" />
                <span style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 12,
                  color: "#6B7280",
                }}>Collapse</span>
              </>
            )}
          </button>
          
          {/* Admin Avatar */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: 8,
          }}>
            <img 
              src="https://i.pravatar.cc/150?img=60" 
              alt="Admin"
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                cursor: "pointer",
              }}
              onClick={() => navigate("/admin/settings")}
            />
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
        {/* Topbar */}
        <header style={{
          height: 64,
          background: "#F7F8F5",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 32px",
          flexShrink: 0,
        }}>
          <div>
            <h1 style={{
              margin: 0,
              fontFamily: "'Playfair Display', serif",
              fontSize: 24,
              fontWeight: 600,
              color: "#111827",
              letterSpacing: "-0.3px",
            }}>
              {meta.title}
            </h1>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              color: "#6B7280",
              marginTop: 4,
            }}>
              {meta.sub}
            </div>
          </div>
          
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Search Input */}
            <div style={{ position: "relative" }}>
              <Search size={14} style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9CA3AF",
              }} />
              <input
                type="text"
                placeholder="Search users, jobs, payments…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearch}
                style={{
                  width: 280,
                  height: 36,
                  paddingLeft: 36,
                  paddingRight: 12,
                  borderRadius: 10,
                  background: "#F3F4F6",
                  border: "none",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  color: "#111827",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
            
            {/* Notifications Dropdown */}
            <NotificationsDropdown />
            
            {/* Avatar */}
            <img
              src="https://i.pravatar.cc/150?img=60"
              alt="Admin"
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                cursor: "pointer",
              }}
              onClick={() => navigate("/admin/settings")}
            />
          </div>
        </header>

        {/* Main Content with padding */}
        <main style={{
          flex: 1,
          overflowY: "auto",
          padding: "28px 32px",
          minWidth: 0,
          boxSizing: "border-box",
        }}>
          <Outlet context={{ 
            showToast, 
            openDrawer, 
            closeDrawer,
            activeDrawer,
            adminRole,
            hasPermission: (permission) => {
              // Simple permission check - admin has all permissions
              return adminRole === 'admin';
            }
          }} />
        </main>
      </div>

      {/* Global Toast */}
      {toast && (
        <div style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 9999,
          background: toast.type === "error" ? "#DC2626" : toast.type === "warning" ? "#D97706" : "#0F172A",
          color: "#FFFFFF",
          padding: "12px 20px",
          borderRadius: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 13,
          fontWeight: 600,
          animation: "slideUp 0.3s ease"
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}